<template>
	<v-hover v-slot="{ isHovering, props: hoverProps }">
		<span v-bind="hoverProps" class="qc-help-icon d-inline-flex align-center">
			<v-icon
				v-bind="$attrs"
				:size="size"
				:color="isHovering ? hoverColor : color"
				:aria-label="ariaLabel"
				tabindex="0"
			>
				{{ icon }}
			</v-icon>
			<v-tooltip
				activator="parent"
				:location="location"
				:open-delay="openDelay"
				:close-delay="interactive ? 200 : 0"
				:interactive="interactive"
				:disabled="!hasContent"
				:max-width="maxWidth"
			>
				<div class="qc-help-icon-tooltip-body">
					<slot>
						<div v-if="text" class="text-body-2">{{ text }}</div>
					</slot>
					<a
						v-if="learnMoreHref"
						:href="learnMoreHref"
						target="_blank"
						rel="noopener noreferrer"
						class="qc-help-icon-learn-more text-caption font-weight-medium d-inline-flex align-center mt-2"
					>
						Learn more
						<v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
					</a>
				</div>
			</v-tooltip>
		</span>
	</v-hover>
</template>

<script>
/**
 * Circled-question-mark help affordance for confusing QC features.
 *
 * Shape mirrors VMetadataHoverIcon (v-hover -> v-tooltip -> v-icon,
 * `inheritAttrs: false`) but is QC-specific by design: the project convention
 * favors explicit, non-overloaded components over editing the shared one.
 *
 * The dominant use is a one-liner via the `text` prop. Set `learnMoreHref` to
 * surface a "Learn more" link to the QC user manual. The default slot accepts
 * structured content for richer cases (none needed yet).
 */
export default {
	name: "QcHelpIcon",
	inheritAttrs: false,

	props: {
		/** One-liner help text (the common case). */
		text: { type: String, default: "" },
		/** When set, the tooltip shows a "Learn more" link to this URL. */
		learnMoreHref: { type: String, default: "" },
		/** Icon glyph. */
		icon: { type: String, default: "mdi-help-circle-outline" },
		/** Icon size: a Vuetify size name or a pixel number. */
		size: { type: [String, Number], default: "x-small" },
		/** Icon colour at rest. */
		color: { type: String, default: "grey-darken-1" },
		/** Icon colour while hovered. */
		hoverColor: { type: String, default: "secondary" },
		/** Tooltip anchor side. */
		location: { type: String, default: "top" },
		/** Hover delay before the tooltip opens, in ms. */
		openDelay: { type: [Number, String], default: 250 },
		/** Max tooltip width. */
		maxWidth: { type: [Number, String], default: 320 },
	},

	computed: {
		hasContent() {
			return Boolean(this.text || this.learnMoreHref || this.$slots.default);
		},

		// An interactive tooltip keeps pointer-events on its content (Vuetify
		// drops the `:not(.v-tooltip--interactive)` pointer-events:none rule) and
		// stays open while hovered, so the "Learn more" link is clickable.
		interactive() {
			return Boolean(this.learnMoreHref);
		},

		ariaLabel() {
			return this.text || "Help";
		},
	},
};
</script>

<!--
	Not scoped: the "Learn more" link lives in tooltip content that Vuetify
	teleports to the overlay root, outside this component's DOM, so a scoped rule
	could not reach it. The class is specific enough that global scope is safe.
-->
<style lang="scss">
.qc-help-icon {
	cursor: help;
	line-height: 0;
}

.qc-help-icon-learn-more {
	color: #ffce85; // readable accent on the dark tooltip background
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
}
</style>
